import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Modal, ModalBody, ModalHeader } from '@this/components/shared/ui/feedbacks/modal';
import { Loading } from '@this/components/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { FormControl, InputLabel } from '@this/shared/ui/inputs/form_control';
import { Fetcher } from '@this/src/util';
import OrganizationOperationRule, {
  convertOrganizationOperationRuleResponseToArgs,
  type OrganizationOperationRuleResponseArgs
} from '@this/src/domain/organization/organization_operation_rule';
import type OrganizationOperationRuleField from '@this/src/domain/organization/organization_operation_rule_field';

type ShowOperationRuleResponse = {
  operation_rule: OrganizationOperationRuleResponseArgs | null;
};

interface Props {
  show: boolean;
  organizationId: number;
  onClose: () => void;
}

const DISPLAY_FIELD_API_NAMES = [
  'Name',
  'AccountName__c',
  'regulationcharge_hotel__c',
  'regulationcharge_airticekt__c',
  'regulationcharge_package__c',
  'Itinerary_soldout__c',
  'Priceincrease_under_regulationcharge__c',
  'Priceincrease_atleast_regulationcharge__c',
  'Itinerary_addition__c',
  'Itinerary_change__c',
  'hotel_operation_beyondregulation__c',
  'hotel_operation_regulation__c',
  'memo__c',
  'EnactmentDate__c',
  'EffectiveDate__c'
] as const;

const EXCLUDED_FIELD_API_NAMES = new Set([
  'OwnerId',
  'Status__c',
  'hotel_rule_beyondregulation__c',
  'hotel_breakfast__c',
  'airticekt_SeatType__c'
]);

const isDeprecatedFieldLabel = (label: string): boolean =>
  label.startsWith('【廃止】') && !label.startsWith('【廃止予定】');

const displayFields = (fields: OrganizationOperationRuleField[]): OrganizationOperationRuleField[] => {
  const fieldByApiName = new Map(fields.map(field => [field.apiName, field]));

  return DISPLAY_FIELD_API_NAMES.map(apiName => fieldByApiName.get(apiName))
    .filter((field): field is OrganizationOperationRuleField => field !== undefined)
    .filter(field => !EXCLUDED_FIELD_API_NAMES.has(field.apiName))
    .filter(field => !isDeprecatedFieldLabel(field.label));
};

const OperationRuleModal: React.FC<Props> = ({ show, organizationId, onClose }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [loading, setLoading] = useState(false);
  const [operationRule, setOperationRule] = useState<OrganizationOperationRule | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchOperationRule = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      const res = await Fetcher.get<ShowOperationRuleResponse>(
        `/arrangement/organizations/${organizationId}/operation_rule.json`
      );
      setOperationRule(
        res.operation_rule
          ? new OrganizationOperationRule(convertOrganizationOperationRuleResponseToArgs(res.operation_rule))
          : null
      );
    } catch {
      setErrorMessage('運用ルールの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (show && organizationId) {
      fetchOperationRule();
    }
  }, [show, organizationId, fetchOperationRule]);

  const visibleFields = useMemo(() => (operationRule ? displayFields(operationRule.fields) : []), [operationRule]);

  if (!show) return null;

  return (
    <Modal open={show} onClose={onClose} size="large">
      {loading ? (
        <Loading />
      ) : (
        <>
          {errorMessage && (
            <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
              {errorMessage}
            </Alert>
          )}
          {!errorMessage &&
            (operationRule ? (
              <>
                <ModalHeader>運用ルール</ModalHeader>
                <ModalBody style={styles.wrapper}>
                  {visibleFields.map(field => (
                    <FormControl key={field.apiName} style={styles.inputWrapper} fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <div style={styles.fieldValue}>{field.value}</div>
                    </FormControl>
                  ))}
                </ModalBody>
              </>
            ) : (
              <>
                <ModalHeader>運用ルール</ModalHeader>
                <ModalBody>
                  <p>運用ルールが登録されていません</p>
                </ModalBody>
              </>
            ))}
        </>
      )}
    </Modal>
  );
};

const getStyles = (theme: AITTheme): { [key: string]: React.CSSProperties } => ({
  wrapper: {
    maxHeight: '70vh',
    overflowY: 'auto'
  },
  inputWrapper: {
    marginBottom: 10
  },
  fieldValue: {
    whiteSpace: 'pre-wrap',
    display: 'block'
  }
});

export default OperationRuleModal;
